// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MOCK_SOPS } from '../mockData';
import type { SOP, SOPVersion } from '../../../types';
import './SOPVersionHistory.css';

const INITIAL_VISIBLE_COUNT = 4;

function SOPVersionHistory() {
  const { site_id, sopId } = useParams();
  const [showEarlierVersions, setShowEarlierVersions] = useState(false);

  const sop = useMemo<SOP | undefined>(() => MOCK_SOPS.find(s => s.id === sopId), [sopId]);

  const versions = useMemo<SOPVersion[]>(() => (sop ? sop.versions : []), [sop]);

  if (!sop) {
    return (
      <div className="sop-version-history-container">
        <Link to={`/site/${site_id}/sop`} className="sop-back-link">
          &larr; All SOPs
        </Link>
        <p>SOP not found.</p>
      </div>
    );
  }

  const visibleVersions = showEarlierVersions ? versions : versions.slice(0, INITIAL_VISIBLE_COUNT);

  const hiddenCount = versions.length - INITIAL_VISIBLE_COUNT;
  const hasHiddenVersions = versions.length > INITIAL_VISIBLE_COUNT;

  const hiddenVersions = versions.slice(INITIAL_VISIBLE_COUNT);
  const firstHidden = hiddenVersions.length > 0 ? hiddenVersions[hiddenVersions.length - 1] : null;
  const lastHidden = hiddenVersions.length > 0 ? hiddenVersions[0] : null;

  return (
    <div className="sop-version-history-container">
      <Link to={`/site/${site_id}/sop/${sopId}`} className="sop-back-link">
        &larr; {sop.name}
      </Link>

      <h2>Version History</h2>

      <div className="version-card-list">
        {visibleVersions.map(version => (
          <div key={version.id} className="version-card">
            <div className="version-card-header">
              <span className="version-number">{version.versionNumber}</span>
              <span
                className={`version-status-badge version-status-${version.status === 'Active' ? 'active' : 'superseded'}`}
              >
                {version.status}
              </span>
            </div>
            <p className="version-card-date">
              {version.date} &middot; {version.author}
            </p>
            <p className="version-card-reason">
              <em>&ldquo;{version.changeReason}&rdquo;</em>
            </p>
            <p className="version-card-approver">
              Approved: {version.approvalDate} by {version.approver}
            </p>
            <p className="version-card-executions">
              Used in: {version.executionCount} execution{version.executionCount !== 1 ? 's' : ''}
            </p>
            <div className="version-card-actions">
              <Link
                to={`/site/${site_id}/sop/${sopId}/versions/${version.id}`}
                className="version-action-btn version-view-btn"
              >
                View
              </Link>
              <Link
                to={`/site/${site_id}/sop/${sopId}/versions/${version.id}?mode=diff`}
                className="version-action-btn version-diff-btn"
              >
                Diff
              </Link>
            </div>
          </div>
        ))}
      </div>

      {hasHiddenVersions && (
        <button
          className="version-toggle-btn"
          onClick={() => setShowEarlierVersions(!showEarlierVersions)}
        >
          {showEarlierVersions
            ? '\u25b4 Hide earlier versions'
            : `\u25be Show ${hiddenCount} earlier versions (${firstHidden?.versionNumber} \u2013 ${lastHidden?.versionNumber})`}
        </button>
      )}
    </div>
  );
}

export default SOPVersionHistory;
